"use client";

import React, { useRef, useState } from "react";
import { useGSAP } from "@gsap/react";
import { gsap } from "@/lib/gsap";
import { Button } from "@/components/ui/button";
import { XIcon } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";

const ProjectDeliveringAnimation = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showMagik, setShowMagik] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);
  const expandTimeline = useRef<gsap.core.Timeline>(null);

  // Animated Box
  const animatedBoxRef = useRef<SVGRectElement>(null);
  // Animated Box Variants
  const animatedBoxOutlineVariantRef = useRef<SVGPathElement>(null);
  const animatedBoxStrokeVariantRef = useRef<SVGPathElement>(null);
  const animatedBoxLogoVariantRef = useRef<SVGPathElement>(null);
  const animatedBoxFinalLogoVariantRef = useRef<SVGPathElement>(null);
  // Indicators
  const canvasIndicatorRef = useRef<SVGCircleElement>(null);
  const conceptIndicatorRef = useRef<SVGCircleElement>(null);
  const designIndicatorRef = useRef<SVGCircleElement>(null);
  const stylingIndicatorRef = useRef<SVGCircleElement>(null);
  const refinementIndicatorRef = useRef<SVGCircleElement>(null);

  useGSAP(() => {
    const tl = gsap.timeline({
      paused: true,
      onReverseComplete: () => {
        document.body.style.overflow = "auto";
        setShowMagik(true);
      },
    });

    tl.to(
      "#hero-section",
      {
        duration: 1,
        ease: "power2.inOut",
        y: -100,
        filter: "blur(5px)",
        opacity: 0,
      },
      "<",
    )
      .to(
        "#main-section",
        {
          duration: 1,
          ease: "power2.inOut",
          y: 100,
          filter: "blur(5px)",
          opacity: 0,
        },
        "<",
      )
      .to(
        "#navigation-menu",
        {
          duration: 1,
          ease: "power2.inOut",
          y: 100,
          filter: "blur(5px)",
          opacity: 0,
        },
        "<",
      )
      .to(containerRef.current, {
        duration: 1,
        ease: "power2.inOut",
        x: -20,
        y: 100,
        rotationY: 20,
        rotationX: 50,
        scale: 1.5,
        z: 50,
      })
      .to("#box", {
        duration: 1,
        ease: "power2.inOut",
        y: -85,
        x: 35,
        z: 50,
      })
      .to(
        [
          canvasIndicatorRef.current,
          conceptIndicatorRef.current,
          designIndicatorRef.current,
          stylingIndicatorRef.current,
          refinementIndicatorRef.current,
        ],
        {
          duration: 1,
          delay: -1,
          ease: "power2.inOut",
          x: 25,
          y: -25,
        },
      );

    expandTimeline.current = tl;
  }, []);

  const expandSvg = (mode: boolean) => {
    if (!expandTimeline.current) return;
    setShowMagik(false);
    if (mode) {
      setIsExpanded(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
      document.body.style.overflow = "hidden";
      expandTimeline.current.play();
    } else {
      expandTimeline.current.reverse();
      setIsExpanded(false);
    }
  };

  useGSAP(() => {
    const tl = gsap.timeline({
      repeat: -1,
      repeatDelay: 1,
    });

    // Animating box from canvas to concept
    tl.fromTo(
      animatedBoxRef.current,
      {
        x: 92,
        opacity: 1,
      },
      {
        x: 339,
        duration: 2.5,
        ease: "power2.inOut",
        onStart: () => {
          handleIndicatorAnimation(canvasIndicatorRef, "off");
        },
        onComplete: () => {
          handleIndicatorAnimation(conceptIndicatorRef, "pending");
        },
      },
    )
      // Enable outlines and change indicators
      .to(animatedBoxOutlineVariantRef.current, {
        opacity: 1,
        duration: 1,
        delay: 1,
        onStart: () => {
          handleIndicatorAnimation(conceptIndicatorRef, "completed");
        },
        onComplete: () => {
          handleIndicatorAnimation(conceptIndicatorRef, "off");
        },
      })
      // Animating box from concept to design
      .fromTo(
        animatedBoxRef.current,
        {
          y: 0,
          opacity: 1,
        },
        {
          y: 175,
          duration: 2,
          ease: "power2.inOut",
          onStart: () => {
            handleIndicatorAnimation(conceptIndicatorRef, "off");
          },
          onComplete: () => {
            handleIndicatorAnimation(designIndicatorRef, "pending");
          },
        },
      )
      // Disable outlines and Enable Stroke Logo
      .to(animatedBoxOutlineVariantRef.current, {
        opacity: 0,
        duration: 0.5,
        ease: "power2.inOut",
      })
      .to(animatedBoxStrokeVariantRef.current, {
        opacity: 1,
        duration: 1,
        delay: 1,
        onStart: () => {
          handleIndicatorAnimation(designIndicatorRef, "completed");
        },
        onComplete: () => {
          handleIndicatorAnimation(designIndicatorRef, "off");
        },
      })
      // Animating box from design to styling
      .fromTo(
        animatedBoxRef.current,
        {
          x: 339,
          opacity: 1,
        },
        {
          x: 92,
          duration: 2.5,
          ease: "power2.inOut",
          onStart: () => {
            handleIndicatorAnimation(designIndicatorRef, "off");
          },
          onComplete: () => {
            handleIndicatorAnimation(stylingIndicatorRef, "pending");
          },
        },
      )
      // Disable stroke logo and enable logo
      .to(animatedBoxStrokeVariantRef.current, {
        opacity: 0,
        duration: 0.5,
        ease: "power2.inOut",
      })
      .to(animatedBoxLogoVariantRef.current, {
        opacity: 1,
        duration: 1,
        delay: 1,
        onStart: () => {
          handleIndicatorAnimation(stylingIndicatorRef, "completed");
        },
        onComplete: () => {
          handleIndicatorAnimation(stylingIndicatorRef, "off");
        },
      })
      // Animating box from styling to refinement
      .fromTo(
        animatedBoxRef.current,
        {
          x: 92,
          opacity: 1,
        },
        {
          x: -169,
          duration: 2.5,
          ease: "power2.inOut",
          onStart: () => {
            handleIndicatorAnimation(stylingIndicatorRef, "off");
          },
          onComplete: () => {
            handleIndicatorAnimation(refinementIndicatorRef, "pending");
          },
        },
      )
      // Disable logo and enable final logo
      .to(animatedBoxLogoVariantRef.current, {
        opacity: 0,
        duration: 0.5,
        ease: "power2.inOut",
      })
      .to(animatedBoxFinalLogoVariantRef.current, {
        opacity: 1,
        duration: 1,
        delay: 1,
        onStart: () => {
          handleIndicatorAnimation(refinementIndicatorRef, "completed");
        },
        onComplete: () => {
          handleIndicatorAnimation(refinementIndicatorRef, "off");
        },
      })
      // Animating box from refinement to delivery
      .fromTo(
        animatedBoxRef.current,
        {
          x: -169,
          opacity: 1,
        },
        {
          x: -420,
          duration: 2.5,
          ease: "power2.inOut",
          onStart: () => {
            handleIndicatorAnimation(refinementIndicatorRef, "off");
          },
        },
      )
      // now after 1 second delay fade away the animatedBox
      .to(animatedBoxRef.current, {
        opacity: 0,
        duration: 1,
        delay: 1,
        ease: "power2.inOut",
        onComplete: () => {
          handleIndicatorAnimation(canvasIndicatorRef, "default");
        },
      });

    return () => {
      tl.kill();
    };
  }, []);

  return (
    <div className="relative">
      <AnimatePresence mode="sync">
        {showMagik && (
          <motion.div
            layout
            key="click-me-to-see-magik"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1 }}
            onClick={() => expandSvg(true)}
            className="absolute -right-20 z-10 hidden cursor-pointer text-muted-foreground/60 duration-200 hover:text-white sm:block"
          >
            <div className="max-w-20 -rotate-45 text-center text-xs">Click me to see magik</div>
          </motion.div>
        )}
        {isExpanded && (
          <Button
            key="close-magik"
            onClick={() => expandSvg(false)}
            className="fixed right-4 top-4 animate-in fade-in-10"
            size="icon"
          >
            <XIcon />
          </Button>
        )}
      </AnimatePresence>
      <div
        ref={containerRef}
        className="figtree-font pointer-events-none mt-0 select-none will-change-transform sm:-mt-32 sm:h-60"
      >
        <svg
          width="100%"
          height="100%"
          viewBox="0 0 843 304"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="xMidYMid meet"
          overflow="visible"
        >
          {/* Connection Lines */}
          <g>
            <line x1="585" y1="61.5" x2="766" y2="61.5" stroke="#2D2E2F" strokeDasharray="4 4" />
            <line x1="799.5" y1="204" x2="799.5" y2="95" stroke="#2D2E2F" strokeDasharray="4 4" />
            <line x1="585" y1="236.5" x2="766" y2="236.5" stroke="#2D2E2F" strokeDasharray="4 4" />
            <line x1="324" y1="236.5" x2="519" y2="236.5" stroke="#2D2E2F" strokeDasharray="4 4" />
            <line x1="72" y1="236.5" x2="258" y2="236.5" stroke="#2D2E2F" strokeDasharray="4 4" />
          </g>
          {/* Connectors */}
          <g>
            {/* Canvas */}
            <path
              d="M582 59H583.5C584.881 59 586 60.1193 586 61.5V61.5C586 62.8807 584.881 64 583.5 64H582V59Z"
              fill="#2D2E2F"
            />
            {/* Concept */}
            <path
              d="M769 59H767.5C766.119 59 765 60.1193 765 61.5V61.5C765 62.8807 766.119 64 767.5 64H769V59Z"
              fill="#2D2E2F"
            />
            <path
              d="M797 92V93.5C797 94.8807 798.119 96 799.5 96V96C800.881 96 802 94.8807 802 93.5V92H797Z"
              fill="#2D2E2F"
            />
            {/* Design */}
            <path
              d="M802 207V205.5C802 204.119 800.881 203 799.5 203V203C798.119 203 797 204.119 797 205.5V207H802Z"
              fill="#555656"
            />
            <path
              d="M769 234H767.5C766.119 234 765 235.119 765 236.5V236.5C765 237.881 766.119 239 767.5 239H769V234Z"
              fill="#2D2E2F"
            />
            {/* Styling */}
            <path
              d="M522 234H520.5C519.119 234 518 235.119 518 236.5V236.5C518 237.881 519.119 239 520.5 239H522V234Z"
              fill="#2D2E2F"
            />
            <path
              d="M582 239H583.5C584.881 239 586 237.881 586 236.5V236.5C586 235.119 584.881 234 583.5 234H582V239Z"
              fill="#2D2E2F"
            />
            {/* Refinement */}
            <path
              d="M261 234H259.5C258.119 234 257 235.119 257 236.5V236.5C257 237.881 258.119 239 259.5 239H261V234Z"
              fill="#2D2E2F"
            />
            <path
              d="M321 239H322.5C323.881 239 325 237.881 325 236.5V236.5C325 235.119 323.881 234 322.5 234H321V239Z"
              fill="#2D2E2F"
            />
            {/* Delivery */}
            <path
              d="M69 239H70.5C71.8807 239 73 237.881 73 236.5V236.5C73 235.119 71.8807 234 70.5 234H69V239Z"
              fill="#2D2E2F"
            />
          </g>
          {/* Hidden raw boxes */}
          <g fill="#0E0F10" stroke="#2D2E2F">
            <rect x="522.5" y="32.5" width="59" height="59" rx="9.5" strokeDasharray="4 4" />
            <rect x="769.5" y="32.5" width="59" height="59" rx="9.5" strokeDasharray="4 4" />
            <rect x="769.5" y="207.5" width="59" height="59" rx="9.5" strokeDasharray="4 4" />
            <rect x="522.5" y="207.5" width="59" height="59" rx="9.5" strokeDasharray="4 4" />
            <rect x="261.5" y="207.5" width="59" height="59" rx="9.5" strokeDasharray="4 4" />
          </g>
          {/* Delivery Box */}
          <g>
            <rect
              x="10.5"
              y="207.5"
              width="59"
              height="59"
              rx="9.5"
              fill="#0E0F10"
              stroke="#2D2E2F"
              strokeDasharray="4 4"
            />
            <path
              d="M12.104 283V274.24H14.372C14.94 274.24 15.428 274.348 15.836 274.564C16.252 274.78 16.572 275.084 16.796 275.476C17.028 275.868 17.144 276.332 17.144 276.868V280.36C17.144 280.896 17.028 281.364 16.796 281.764C16.572 282.156 16.252 282.46 15.836 282.676C15.428 282.892 14.94 283 14.372 283H12.104ZM13.184 282.04H14.372C14.9 282.04 15.312 281.892 15.608 281.596C15.912 281.3 16.064 280.888 16.064 280.36V276.868C16.064 276.348 15.912 275.94 15.608 275.644C15.312 275.348 14.9 275.2 14.372 275.2H13.184V282.04ZM21.7953 283.12C21.2753 283.12 20.8193 283.016 20.4273 282.808C20.0433 282.6 19.7433 282.308 19.5273 281.932C19.3193 281.548 19.2153 281.104 19.2153 280.6V278.8C19.2153 278.288 19.3193 277.844 19.5273 277.468C19.7433 277.092 20.0433 276.8 20.4273 276.592C20.8193 276.384 21.2753 276.28 21.7953 276.28C22.3153 276.28 22.7673 276.384 23.1513 276.592C23.5433 276.8 23.8433 277.092 24.0513 277.468C24.2673 277.844 24.3753 278.288 24.3753 278.8V279.964H20.2713V280.6C20.2713 281.12 20.4033 281.52 20.6673 281.8C20.9313 282.072 21.3073 282.208 21.7953 282.208C22.2113 282.208 22.5473 282.136 22.8033 281.992C23.0593 281.84 23.2153 281.616 23.2713 281.32H24.3513C24.2793 281.872 24.0073 282.312 23.5353 282.64C23.0713 282.96 22.4913 283.12 21.7953 283.12ZM23.3193 279.244V278.8C23.3193 278.28 23.1873 277.88 22.9233 277.6C22.6673 277.32 22.2913 277.18 21.7953 277.18C21.3073 277.18 20.9313 277.32 20.6673 277.6C20.4033 277.88 20.2713 278.28 20.2713 278.8V279.148H23.4033L23.3193 279.244ZM29.8906 283C29.5066 283 29.1706 282.924 28.8826 282.772C28.5946 282.62 28.3706 282.404 28.2106 282.124C28.0506 281.844 27.9706 281.516 27.9706 281.14V275.224H25.7506V274.24H29.0506V281.14C29.0506 281.412 29.1266 281.628 29.2786 281.788C29.4306 281.94 29.6346 282.016 29.8906 282.016H31.9906V283H29.8906ZM33.6059 283V282.016H35.9459V277.384H33.9059V276.4H37.0259V282.016H39.2459V283H33.6059ZM36.3659 275.212C36.1019 275.212 35.8939 275.144 35.7419 275.008C35.5899 274.872 35.5139 274.688 35.5139 274.456C35.5139 274.216 35.5899 274.028 35.7419 273.892C35.8939 273.748 36.1019 273.676 36.3659 273.676C36.6299 273.676 36.8379 273.748 36.9899 273.892C37.1419 274.028 37.2179 274.216 37.2179 274.456C37.2179 274.688 37.1419 274.872 36.9899 275.008C36.8379 275.144 36.6299 275.212 36.3659 275.212ZM42.6493 283L40.4413 276.4H41.5452L43.0093 280.84C43.0973 281.104 43.1773 281.36 43.2493 281.608C43.3213 281.848 43.3733 282.036 43.4053 282.172C43.4453 282.036 43.5013 281.848 43.5733 281.608C43.6453 281.36 43.7213 281.104 43.8013 280.84L45.2293 276.4H46.3213L44.1133 283H42.6493ZM50.5766 283.12C50.0566 283.12 49.6006 283.016 49.2086 282.808C48.8246 282.6 48.5246 282.308 48.3086 281.932C48.1006 281.548 47.9966 281.104 47.9966 280.6V278.8C47.9966 278.288 48.1006 277.844 48.3086 277.468C48.5246 277.092 48.8246 276.8 49.2086 276.592C49.6006 276.384 50.0566 276.28 50.5766 276.28C51.0966 276.28 51.5486 276.384 51.9326 276.592C52.3246 276.8 52.6246 277.092 52.8326 277.468C53.0486 277.844 53.1566 278.288 53.1566 278.8V279.964H49.0526V280.6C49.0526 281.12 49.1846 281.52 49.4486 281.8C49.7126 282.072 50.0886 282.208 50.5766 282.208C50.9926 282.208 51.3286 282.136 51.5846 281.992C51.8406 281.84 51.9966 281.616 52.0526 281.32H53.1326C53.0606 281.872 52.7886 282.312 52.3166 282.64C51.8526 282.96 51.2726 283.12 50.5766 283.12ZM52.1006 279.244V278.8C52.1006 278.28 51.9686 277.88 51.7046 277.6C51.4486 277.32 51.0726 277.18 50.5766 277.18C50.0886 277.18 49.7126 277.32 49.4486 277.6C49.1846 277.88 49.0526 278.28 49.0526 278.8V279.148H52.1846L52.1006 279.244ZM55.5039 283V276.4H56.5599V277.66H56.8119L56.4639 278.44C56.4639 277.728 56.6159 277.192 56.9199 276.832C57.2319 276.464 57.6919 276.28 58.2999 276.28C58.9959 276.28 59.5479 276.5 59.9559 276.94C60.3719 277.372 60.5799 277.96 60.5799 278.704V279.1H59.4999V278.8C59.4999 278.28 59.3719 277.884 59.1159 277.612C58.8679 277.332 58.5119 277.192 58.0479 277.192C57.5919 277.192 57.2319 277.332 56.9679 277.612C56.7119 277.892 56.5839 278.288 56.5839 278.8V283H55.5039ZM63.5032 285.16L64.4752 282.592L62.0272 276.4H63.2272L64.7632 280.48C64.8192 280.616 64.8712 280.78 64.9192 280.972C64.9672 281.156 65.0072 281.312 65.0392 281.44C65.0712 281.312 65.1112 281.156 65.1592 280.972C65.2072 280.78 65.2552 280.616 65.3032 280.48L66.7432 276.4H67.9072L64.6552 285.16H63.5032Z"
              fill="#444449"
            />
          </g>
          {/* Animated Box */}
          <g ref={animatedBoxRef} style={{ willChange: "transform", opacity: 0 }}>
            {/* Box */}
            <g>
              <rect
                x="430.5"
                y="32.5"
                width="59"
                height="59"
                rx="9.5"
                fill="#0E0F10"
                stroke="#2D2E2F"
              />
            </g>
            {/* Lines */}
            <g
              ref={animatedBoxOutlineVariantRef}
              style={{ willChange: "transform", opacity: 0 }}
              stroke="#2D2E2F"
              strokeWidth="0.4"
            >
              <line x1="430.823" y1="70.8232" x2="468.823" y2="32.8232" />
              <line x1="430.823" y1="78.8232" x2="476.823" y2="32.8232" />
              <line x1="441.825" y1="91.8213" x2="489.825" y2="44.8213" />
              <line x1="449.825" y1="91.821" x2="489.825" y2="52.821" />
              <line x1="430.454" y1="52.5475" x2="469.178" y2="91.8245" />
              <line x1="431.177" y1="44.8232" x2="478.177" y2="91.8232" />
              <line x1="436.177" y1="33.8232" x2="488.177" y2="85.8232" />
              <line x1="432.177" y1="37.8232" x2="484.177" y2="89.8232" />
              <line x1="442.177" y1="32.8232" x2="489.177" y2="79.8232" />
              <line x1="451.179" y1="32.8255" x2="489.179" y2="71.8255" />
            </g>
            {/* Logo Stroke */}
            <g ref={animatedBoxStrokeVariantRef} style={{ willChange: "transform", opacity: 0 }}>
              <path
                d="M459.88 42V49.9042L447.904 61.8802L459.88 73.8563H451.737L440 61.8802L459.88 42Z"
                stroke="#3B3D3F"
                strokeWidth="0.5"
              />
              <path
                d="M459.88 49.9042H467.784L480 62.1198H471.856L459.88 49.9042Z"
                stroke="#3B3D3F"
                strokeWidth="0.5"
              />
              <path
                d="M459.88 73.8563V81.7605L471.856 69.7844L459.88 57.8084V65.9521L463.952 69.9042L459.88 73.8563Z"
                stroke="#3B3D3F"
                strokeWidth="0.5"
              />
            </g>
            {/* Logo */}
            <g ref={animatedBoxLogoVariantRef} style={{ willChange: "transform", opacity: 0 }}>
              <path
                d="M459.88 42V49.9042L447.904 61.8802L459.88 73.8563H451.737L440 61.8802L459.88 42Z"
                fill="#3B3D3F"
              />
              <path
                d="M459.88 49.9042H467.784L480 62.1198H471.856L459.88 49.9042Z"
                fill="#3B3D3F"
              />
              <path
                d="M459.88 73.8563V81.7605L471.856 69.7844L459.88 57.8084V65.9521L463.952 69.9042L459.88 73.8563Z"
                fill="#3B3D3F"
              />
            </g>
            {/* Final Logo */}
            <g ref={animatedBoxFinalLogoVariantRef} style={{ willChange: "transform", opacity: 0 }}>
              <path
                d="M459.88 42V49.9042L447.904 61.8802L459.88 73.8563H451.737L440 61.8802L459.88 42Z"
                fill="white"
              />
              <path
                d="M459.88 49.9042H467.784L480 62.1198H471.856L459.88 49.9042Z"
                fill="#4C4C4C"
              />
              <path
                d="M459.88 73.8563V81.7605L471.856 69.7844L459.88 57.8084V65.9521L463.952 69.9042L459.88 73.8563Z"
                fill="#C7C7C7"
              />
            </g>
          </g>
          {/* All texts */}
          <g>
            <path
              d="M533.648 24.12C533.12 24.12 532.66 24.02 532.268 23.82C531.884 23.62 531.584 23.332 531.368 22.956C531.16 22.572 531.056 22.12 531.056 21.6V17.64C531.056 17.112 531.16 16.66 531.368 16.284C531.584 15.908 531.884 15.62 532.268 15.42C532.66 15.22 533.12 15.12 533.648 15.12C534.176 15.12 534.632 15.224 535.016 15.432C535.4 15.632 535.696 15.92 535.904 16.296C536.112 16.672 536.216 17.12 536.216 17.64H535.136C535.136 17.144 535.004 16.764 534.74 16.5C534.484 16.228 534.12 16.092 533.648 16.092C533.176 16.092 532.804 16.224 532.532 16.488C532.268 16.752 532.136 17.132 532.136 17.628V21.6C532.136 22.096 532.268 22.48 532.532 22.752C532.804 23.016 533.176 23.148 533.648 23.148C534.12 23.148 534.484 23.016 534.74 22.752C535.004 22.48 535.136 22.096 535.136 21.6H536.216C536.216 22.112 536.112 22.56 535.904 22.944C535.696 23.32 535.4 23.612 535.016 23.82C534.632 24.02 534.176 24.12 533.648 24.12ZM540.219 24.12C539.523 24.12 538.975 23.944 538.575 23.592C538.175 23.232 537.975 22.748 537.975 22.14C537.975 21.524 538.175 21.04 538.575 20.688C538.975 20.336 539.515 20.16 540.195 20.16H542.235V19.5C542.235 19.092 542.111 18.78 541.863 18.564C541.623 18.34 541.279 18.228 540.831 18.228C540.439 18.228 540.111 18.312 539.847 18.48C539.583 18.64 539.431 18.86 539.391 19.14H538.311C538.383 18.572 538.651 18.12 539.115 17.784C539.579 17.448 540.159 17.28 540.855 17.28C541.615 17.28 542.215 17.476 542.655 17.868C543.095 18.26 543.315 18.792 543.315 19.464V24H542.259V22.74H542.079L542.259 22.56C542.259 23.04 542.071 23.42 541.695 23.7C541.327 23.98 540.835 24.12 540.219 24.12ZM540.483 23.268C540.995 23.268 541.415 23.14 541.743 22.884C542.071 22.628 542.235 22.3 542.235 21.9V20.964H540.219C539.859 20.964 539.575 21.064 539.367 21.264C539.159 21.464 539.055 21.736 539.055 22.08C539.055 22.448 539.179 22.74 539.427 22.956C539.683 23.164 540.035 23.268 540.483 23.268ZM545.495 24V17.4H546.575V18.66H546.803L546.575 18.912C546.575 18.392 546.731 17.992 547.043 17.712C547.355 17.424 547.787 17.28 548.339 17.28C549.003 17.28 549.531 17.484 549.923 17.892C550.315 18.3 550.511 18.856 550.511 19.56V24H549.431V19.68C549.431 19.208 549.303 18.848 549.047 18.6C548.799 18.344 548.459 18.216 548.027 18.216C547.579 18.216 547.223 18.352 546.959 18.624C546.703 18.896 546.575 19.288 546.575 19.8V24H545.495ZM554.454 24L552.246 17.4H553.35L554.814 21.84C554.902 22.104 554.982 22.36 555.054 22.608C555.126 22.848 555.178 23.036 555.21 23.172C555.25 23.036 555.306 22.848 555.378 22.608C555.45 22.36 555.526 22.104 555.606 21.84L557.034 17.4H558.126L555.918 24H554.454ZM561.805 24.12C561.109 24.12 560.561 23.944 560.161 23.592C559.761 23.232 559.561 22.748 559.561 22.14C559.561 21.524 559.761 21.04 560.161 20.688C560.561 20.336 561.101 20.16 561.781 20.16H563.821V19.5C563.821 19.092 563.697 18.78 563.449 18.564C563.209 18.34 562.865 18.228 562.417 18.228C562.025 18.228 561.697 18.312 561.433 18.48C561.169 18.64 561.017 18.86 560.977 19.14H559.897C559.969 18.572 560.237 18.12 560.701 17.784C561.165 17.448 561.745 17.28 562.441 17.28C563.201 17.28 563.801 17.476 564.241 17.868C564.681 18.26 564.901 18.792 564.901 19.464V24H563.845V22.74H563.665L563.845 22.56C563.845 23.04 563.657 23.42 563.281 23.7C562.913 23.98 562.421 24.12 561.805 24.12ZM562.069 23.268C562.581 23.268 563.001 23.14 563.329 22.884C563.657 22.628 563.821 22.3 563.821 21.9V20.964H561.805C561.445 20.964 561.161 21.064 560.953 21.264C560.745 21.464 560.641 21.736 560.641 22.08C560.641 22.448 560.765 22.74 561.013 22.956C561.269 23.164 561.621 23.268 562.069 23.268ZM569.373 24.096C568.925 24.096 568.533 24.028 568.197 23.892C567.869 23.748 567.605 23.548 567.405 23.292C567.213 23.036 567.097 22.732 567.057 22.38H568.137C568.185 22.612 568.313 22.796 568.521 22.932C568.737 23.068 569.021 23.136 569.373 23.136H569.877C570.301 23.136 570.617 23.052 570.825 22.884C571.033 22.708 571.137 22.476 571.137 22.188C571.137 21.908 571.041 21.688 570.849 21.528C570.665 21.36 570.389 21.248 570.021 21.192L569.133 21.048C568.485 20.936 568.001 20.736 567.681 20.448C567.369 20.152 567.213 19.724 567.213 19.164C567.213 18.572 567.401 18.116 567.777 17.796C568.153 17.468 568.717 17.304 569.469 17.304H569.925C570.557 17.304 571.065 17.452 571.449 17.748C571.833 18.044 572.065 18.444 572.145 18.948H571.065C571.017 18.74 570.897 18.576 570.705 18.456C570.513 18.328 570.253 18.264 569.925 18.264H569.469C569.061 18.264 568.761 18.34 568.569 18.492C568.385 18.644 568.293 18.872 568.293 19.176C568.293 19.448 568.373 19.648 568.533 19.776C568.693 19.904 568.945 19.996 569.289 20.052L570.177 20.196C570.889 20.308 571.405 20.516 571.725 20.82C572.053 21.124 572.217 21.564 572.217 22.14C572.217 22.748 572.021 23.228 571.629 23.58C571.245 23.924 570.661 24.096 569.877 24.096H569.373Z"
              fill="#444449"
            />
            <path
              d="M776.648 24.12C776.12 24.12 775.66 24.02 775.268 23.82C774.884 23.62 774.584 23.332 774.368 22.956C774.16 22.572 774.056 22.12 774.056 21.6V17.64C774.056 17.112 774.16 16.66 774.368 16.284C774.584 15.908 774.884 15.62 775.268 15.42C775.66 15.22 776.12 15.12 776.648 15.12C777.176 15.12 777.632 15.224 778.016 15.432C778.4 15.632 778.696 15.92 778.904 16.296C779.112 16.672 779.216 17.12 779.216 17.64H778.136C778.136 17.144 778.004 16.764 777.74 16.5C777.484 16.228 777.12 16.092 776.648 16.092C776.176 16.092 775.804 16.224 775.532 16.488C775.268 16.752 775.136 17.132 775.136 17.628V21.6C775.136 22.096 775.268 22.48 775.532 22.752C775.804 23.016 776.176 23.148 776.648 23.148C777.12 23.148 777.484 23.016 777.74 22.752C778.004 22.48 778.136 22.096 778.136 21.6H779.216C779.216 22.112 779.112 22.56 778.904 22.944C778.696 23.32 778.4 23.612 778.016 23.82C777.632 24.02 777.176 24.12 776.648 24.12ZM783.795 24.096C783.267 24.096 782.811 23.996 782.427 23.796C782.043 23.596 781.743 23.308 781.527 22.932C781.319 22.548 781.215 22.096 781.215 21.576V19.824C781.215 19.296 781.319 18.844 781.527 18.468C781.743 18.092 782.043 17.804 782.427 17.604C782.811 17.404 783.267 17.304 783.795 17.304C784.323 17.304 784.779 17.404 785.163 17.604C785.547 17.804 785.843 18.092 786.051 18.468C786.267 18.844 786.375 19.292 786.375 19.812V21.576C786.375 22.096 786.267 22.548 786.051 22.932C785.843 23.308 785.547 23.596 785.163 23.796C784.779 23.996 784.323 24.096 783.795 24.096ZM783.795 23.136C784.267 23.136 784.635 23.004 784.899 22.74C785.163 22.476 785.295 22.088 785.295 21.576V19.824C785.295 19.312 785.163 18.924 784.899 18.66C784.635 18.396 784.267 18.264 783.795 18.264C783.331 18.264 782.963 18.396 782.691 18.66C782.427 18.924 782.295 19.312 782.295 19.824V21.576C782.295 22.088 782.427 22.476 782.691 22.74C782.963 23.004 783.331 23.136 783.795 23.136ZM788.495 24V17.4H789.575V18.66H789.803L789.575 18.912C789.575 18.392 789.731 17.992 790.043 17.712C790.355 17.424 790.787 17.28 791.339 17.28C792.003 17.28 792.531 17.484 792.923 17.892C793.315 18.3 793.511 18.856 793.511 19.56V24H792.431V19.68C792.431 19.208 792.303 18.848 792.047 18.6C791.799 18.344 791.459 18.216 791.027 18.216C790.579 18.216 790.223 18.352 789.959 18.624C789.703 18.896 789.575 19.288 789.575 19.8V24H788.495ZM798.234 24.12C797.706 24.12 797.242 24.02 796.842 23.82C796.45 23.62 796.146 23.332 795.93 22.956C795.714 22.572 795.606 22.12 795.606 21.6V19.8C795.606 19.272 795.714 18.82 795.93 18.444C796.146 18.068 796.45 17.78 796.842 17.58C797.242 17.38 797.706 17.28 798.234 17.28C798.994 17.28 799.606 17.484 800.07 17.892C800.534 18.3 800.778 18.856 800.802 19.56H799.722C799.698 19.136 799.554 18.812 799.29 18.588C799.034 18.356 798.682 18.24 798.234 18.24C797.762 18.24 797.386 18.376 797.106 18.648C796.826 18.912 796.686 19.292 796.686 19.788V21.6C796.686 22.096 796.826 22.48 797.106 22.752C797.386 23.024 797.762 23.16 798.234 23.16C798.682 23.16 799.034 23.044 799.29 22.812C799.554 22.58 799.698 22.256 799.722 21.84H800.802C800.778 22.544 800.534 23.1 800.07 23.508C799.606 23.916 798.994 24.12 798.234 24.12ZM805.381 24.12C804.861 24.12 804.405 24.016 804.013 23.808C803.629 23.6 803.329 23.308 803.113 22.932C802.905 22.548 802.801 22.104 802.801 21.6V19.8C802.801 19.288 802.905 18.844 803.113 18.468C803.329 18.092 803.629 17.8 804.013 17.592C804.405 17.384 804.861 17.28 805.381 17.28C805.901 17.28 806.353 17.384 806.737 17.592C807.129 17.8 807.429 18.092 807.637 18.468C807.853 18.844 807.961 19.288 807.961 19.8V20.964H803.857V21.6C803.857 22.12 803.989 22.52 804.253 22.8C804.517 23.072 804.893 23.208 805.381 23.208C805.797 23.208 806.133 23.136 806.389 22.992C806.645 22.84 806.801 22.616 806.857 22.32H807.937C807.865 22.872 807.593 23.312 807.121 23.64C806.657 23.96 806.077 24.12 805.381 24.12ZM806.905 20.244V19.8C806.905 19.28 806.773 18.88 806.509 18.6C806.253 18.32 805.877 18.18 805.381 18.18C804.893 18.18 804.517 18.32 804.253 18.6C803.989 18.88 803.857 19.28 803.857 19.8V20.148H806.989L806.905 20.244ZM810.081 26.16V17.4H811.161V18.66H811.377L811.161 18.912C811.161 18.408 811.325 18.012 811.653 17.724C811.989 17.428 812.429 17.28 812.973 17.28C813.637 17.28 814.165 17.5 814.557 17.94C814.957 18.372 815.157 18.968 815.157 19.728V21.66C815.157 22.164 815.065 22.6 814.881 22.968C814.705 23.336 814.453 23.62 814.125 23.82C813.805 24.02 813.421 24.12 812.973 24.12C812.437 24.12 812.001 23.972 811.665 23.676C811.329 23.38 811.161 22.984 811.161 22.488L811.377 22.74H811.137L811.161 24.24V26.16H810.081ZM812.613 23.184C813.069 23.184 813.425 23.048 813.681 22.776C813.945 22.504 814.077 22.112 814.077 21.6V19.8C814.077 19.288 813.945 18.896 813.681 18.624C813.425 18.352 813.069 18.216 812.613 18.216C812.173 18.216 811.821 18.356 811.557 18.636C811.293 18.916 811.161 19.304 811.161 19.8V21.6C811.161 22.096 811.293 22.484 811.557 22.764C811.821 23.044 812.173 23.184 812.613 23.184ZM820.492 24C819.924 24 819.48 23.848 819.16 23.544C818.848 23.24 818.692 22.812 818.692 22.26V18.384H816.832V17.4H818.692V15.54H819.772V17.4H822.412V18.384H819.772V22.26C819.772 22.764 820.012 23.016 820.492 23.016H822.292V24H820.492Z"
              fill="#444449"
            />
            <path
              d="M777.518 283V274.24H779.786C780.354 274.24 780.842 274.348 781.25 274.564C781.666 274.78 781.986 275.084 782.21 275.476C782.442 275.868 782.558 276.332 782.558 276.868V280.36C782.558 280.896 782.442 281.364 782.21 281.764C781.986 282.156 781.666 282.46 781.25 282.676C780.842 282.892 780.354 283 779.786 283H777.518ZM778.598 282.04H779.786C780.314 282.04 780.726 281.892 781.022 281.596C781.326 281.3 781.478 280.888 781.478 280.36V276.868C781.478 276.348 781.326 275.94 781.022 275.644C780.726 275.348 780.314 275.2 779.786 275.2H778.598V282.04ZM787.209 283.12C786.689 283.12 786.233 283.016 785.841 282.808C785.457 282.6 785.157 282.308 784.941 281.932C784.733 281.548 784.629 281.104 784.629 280.6V278.8C784.629 278.288 784.733 277.844 784.941 277.468C785.157 277.092 785.457 276.8 785.841 276.592C786.233 276.384 786.689 276.28 787.209 276.28C787.729 276.28 788.181 276.384 788.565 276.592C788.957 276.8 789.257 277.092 789.465 277.468C789.681 277.844 789.789 278.288 789.789 278.8V279.964H785.685V280.6C785.685 281.12 785.817 281.52 786.081 281.8C786.345 282.072 786.721 282.208 787.209 282.208C787.625 282.208 787.961 282.136 788.217 281.992C788.473 281.84 788.629 281.616 788.685 281.32H789.765C789.693 281.872 789.421 282.312 788.949 282.64C788.485 282.96 787.905 283.12 787.209 283.12ZM788.733 279.244V278.8C788.733 278.28 788.601 277.88 788.337 277.6C788.081 277.32 787.705 277.18 787.209 277.18C786.721 277.18 786.345 277.32 786.081 277.6C785.817 277.88 785.685 278.28 785.685 278.8V279.148H788.817L788.733 279.244ZM794.201 283.096C793.753 283.096 793.361 283.028 793.025 282.892C792.697 282.748 792.433 282.548 792.233 282.292C792.041 282.036 791.925 281.732 791.885 281.38H792.965C793.013 281.612 793.141 281.796 793.349 281.932C793.565 282.068 793.849 282.136 794.201 282.136H794.705C795.129 282.136 795.445 282.052 795.653 281.884C795.861 281.708 795.965 281.476 795.965 281.188C795.965 280.908 795.869 280.688 795.677 280.528C795.493 280.36 795.217 280.248 794.849 280.192L793.961 280.048C793.313 279.936 792.829 279.736 792.509 279.448C792.197 279.152 792.041 278.724 792.041 278.164C792.041 277.572 792.229 277.116 792.605 276.796C792.981 276.468 793.545 276.304 794.297 276.304H794.753C795.385 276.304 795.893 276.452 796.277 276.748C796.661 277.044 796.893 277.444 796.973 277.948H795.893C795.845 277.74 795.725 277.576 795.533 277.456C795.341 277.328 795.081 277.264 794.753 277.264H794.297C793.889 277.264 793.589 277.34 793.397 277.492C793.213 277.644 793.121 277.872 793.121 278.176C793.121 278.448 793.201 278.648 793.361 278.776C793.521 278.904 793.773 278.996 794.117 279.052L795.005 279.196C795.717 279.308 796.233 279.516 796.553 279.82C796.881 280.124 797.045 280.564 797.045 281.14C797.045 281.748 796.849 282.228 796.457 282.58C796.073 282.924 795.489 283.096 794.705 283.096H794.201ZM799.02 283V282.016H801.36V277.384H799.32V276.4H802.44V282.016H804.66V283H799.02ZM801.78 275.212C801.516 275.212 801.308 275.144 801.156 275.008C801.004 274.872 800.928 274.688 800.928 274.456C800.928 274.216 801.004 274.028 801.156 273.892C801.308 273.748 801.516 273.676 801.78 273.676C802.044 273.676 802.252 273.748 802.404 273.892C802.556 274.028 802.632 274.216 802.632 274.456C802.632 274.688 802.556 274.872 802.404 275.008C802.252 275.144 802.044 275.212 801.78 275.212ZM807.127 285.16V284.176H809.227C809.563 284.176 809.807 284.1 809.959 283.948C810.119 283.796 810.199 283.56 810.199 283.24V282.4L810.223 281.2H810.007L810.211 281.02C810.211 281.5 810.051 281.88 809.731 282.16C809.411 282.44 808.983 282.58 808.447 282.58C807.767 282.58 807.231 282.36 806.839 281.92C806.447 281.472 806.251 280.872 806.251 280.12V278.728C806.251 277.976 806.447 277.38 806.839 276.94C807.231 276.5 807.767 276.28 808.447 276.28C808.983 276.28 809.411 276.42 809.731 276.7C810.051 276.98 810.211 277.36 810.211 277.84L810.007 277.66H810.211V276.4H811.279V283.24C811.279 283.832 811.095 284.3 810.727 284.644C810.367 284.988 809.863 285.16 809.215 285.16H807.127ZM808.771 281.644C809.219 281.644 809.571 281.504 809.827 281.224C810.083 280.944 810.211 280.556 810.211 280.06V278.8C810.211 278.304 810.083 277.916 809.827 277.636C809.571 277.356 809.219 277.216 808.771 277.216C808.315 277.216 807.959 277.352 807.703 277.624C807.455 277.896 807.331 278.288 807.331 278.8V280.06C807.331 280.572 807.455 280.964 807.703 281.236C807.959 281.508 808.315 281.644 808.771 281.644ZM813.495 283V276.4H814.575V277.66H814.803L814.575 277.912C814.575 277.392 814.731 276.992 815.043 276.712C815.355 276.424 815.787 276.28 816.339 276.28C817.003 276.28 817.531 276.484 817.923 276.892C818.315 277.3 818.511 277.856 818.511 278.56V283H817.431V278.68C817.431 278.208 817.303 277.848 817.047 277.6C816.799 277.344 816.459 277.216 816.027 277.216C815.579 277.216 815.223 277.352 814.959 277.624C814.703 277.896 814.575 278.288 814.575 278.8V283H813.495ZM770.323 299V290.24H772.591C773.159 290.24 773.647 290.348 774.055 290.564C774.471 290.78 774.791 291.084 775.015 291.476C775.247 291.868 775.363 292.332 775.363 292.868V296.36C775.363 296.896 775.247 297.364 775.015 297.764C774.791 298.156 774.471 298.46 774.055 298.676C773.647 298.892 773.159 299 772.591 299H770.323ZM771.403 298.04H772.591C773.119 298.04 773.531 297.892 773.827 297.596C774.131 297.3 774.283 296.888 774.283 296.36V292.868C774.283 292.348 774.131 291.94 773.827 291.644C773.531 291.348 773.119 291.2 772.591 291.2H771.403V298.04ZM777.746 299V292.4H778.802V293.66H779.054L778.706 294.44C778.706 293.728 778.858 293.192 779.162 292.832C779.474 292.464 779.934 292.28 780.542 292.28C781.238 292.28 781.79 292.5 782.198 292.94C782.614 293.372 782.822 293.96 782.822 294.704V295.1H781.742V294.8C781.742 294.28 781.614 293.884 781.358 293.612C781.11 293.332 780.754 293.192 780.29 293.192C779.834 293.192 779.474 293.332 779.21 293.612C778.954 293.892 778.826 294.288 778.826 294.8V299H777.746ZM786.633 299.12C785.937 299.12 785.389 298.944 784.989 298.592C784.589 298.232 784.389 297.748 784.389 297.14C784.389 296.524 784.589 296.04 784.989 295.688C785.389 295.336 785.929 295.16 786.609 295.16H788.649V294.5C788.649 294.092 788.525 293.78 788.277 293.564C788.037 293.34 787.693 293.228 787.245 293.228C786.853 293.228 786.525 293.312 786.261 293.48C785.997 293.64 785.845 293.86 785.805 294.14H784.725C784.797 293.572 785.065 293.12 785.529 292.784C785.993 292.448 786.573 292.28 787.269 292.28C788.029 292.28 788.629 292.476 789.069 292.868C789.509 293.26 789.729 293.792 789.729 294.464V299H788.673V297.74H788.493L788.673 297.56C788.673 298.04 788.485 298.42 788.109 298.7C787.741 298.98 787.249 299.12 786.633 299.12ZM786.897 298.268C787.409 298.268 787.829 298.14 788.157 297.884C788.485 297.628 788.649 297.3 788.649 296.9V295.964H786.633C786.273 295.964 785.989 296.064 785.781 296.264C785.573 296.464 785.469 296.736 785.469 297.08C785.469 297.448 785.593 297.74 785.841 297.956C786.097 298.164 786.449 298.268 786.897 298.268ZM793.445 299V294.044H791.525V293.06H793.445V291.92C793.445 291.4 793.609 290.992 793.937 290.696C794.265 290.392 794.717 290.24 795.293 290.24H797.225V291.224H795.293C795.045 291.224 794.853 291.284 794.717 291.404C794.589 291.516 794.525 291.688 794.525 291.92V293.06H797.225V294.044H794.525V299H793.445ZM802.32 299C801.752 299 801.308 298.848 800.988 298.544C800.676 298.24 800.52 297.812 800.52 297.26V293.384H798.66V292.4H800.52V290.54H801.6V292.4H804.24V293.384H801.6V297.26C801.6 297.764 801.84 298.016 802.32 298.016H804.12V299H802.32ZM806.215 299V298.016H808.555V293.384H806.515V292.4H809.635V298.016H811.855V299H806.215ZM808.975 291.212C808.711 291.212 808.503 291.144 808.351 291.008C808.199 290.872 808.123 290.688 808.123 290.456C808.123 290.216 808.199 290.028 808.351 289.892C808.503 289.748 808.711 289.676 808.975 289.676C809.239 289.676 809.447 289.748 809.599 289.892C809.751 290.028 809.827 290.216 809.827 290.456C809.827 290.688 809.751 290.872 809.599 291.008C809.447 291.144 809.239 291.212 808.975 291.212ZM813.495 299V292.4H814.575V293.66H814.803L814.575 293.912C814.575 293.392 814.731 292.992 815.043 292.712C815.355 292.424 815.787 292.28 816.339 292.28C817.003 292.28 817.531 292.484 817.923 292.892C818.315 293.3 818.511 293.856 818.511 294.56V299H817.431V294.68C817.431 294.208 817.303 293.848 817.047 293.6C816.799 293.344 816.459 293.216 816.027 293.216C815.579 293.216 815.223 293.352 814.959 293.624C814.703 293.896 814.575 294.288 814.575 294.8V299H813.495ZM821.518 301.16V300.176H823.618C823.954 300.176 824.198 300.1 824.35 299.948C824.51 299.796 824.59 299.56 824.59 299.24V298.4L824.614 297.2H824.398L824.602 297.02C824.602 297.5 824.442 297.88 824.122 298.16C823.802 298.44 823.374 298.58 822.838 298.58C822.158 298.58 821.622 298.36 821.23 297.92C820.838 297.472 820.642 296.872 820.642 296.12V294.728C820.642 293.976 820.838 293.38 821.23 292.94C821.622 292.5 822.158 292.28 822.838 292.28C823.374 292.28 823.802 292.42 824.122 292.7C824.442 292.98 824.602 293.36 824.602 293.84L824.398 293.66H824.602V292.4H825.67V299.24C825.67 299.832 825.486 300.3 825.118 300.644C824.758 300.988 824.254 301.16 823.606 301.16H821.518ZM823.162 297.644C823.61 297.644 823.962 297.504 824.218 297.224C824.474 296.944 824.602 296.556 824.602 296.06V294.8C824.602 294.304 824.474 293.916 824.218 293.636C823.962 293.356 823.61 293.216 823.162 293.216C822.706 293.216 822.35 293.352 822.094 293.624C821.846 293.896 821.722 294.288 821.722 294.8V296.06C821.722 296.572 821.846 296.964 822.094 297.236C822.35 297.508 822.706 297.644 823.162 297.644Z"
              fill="#444449"
            />
            <path
              d="M529.648 283.12C529.072 283.12 528.576 283.024 528.16 282.832C527.752 282.64 527.436 282.364 527.212 282.004C526.988 281.644 526.872 281.216 526.864 280.72H527.944C527.944 281.16 528.092 281.508 528.388 281.764C528.692 282.02 529.112 282.148 529.648 282.148C530.152 282.148 530.544 282.024 530.824 281.776C531.112 281.528 531.256 281.184 531.256 280.744C531.256 280.392 531.16 280.084 530.968 279.82C530.784 279.556 530.516 279.372 530.164 279.268L528.976 278.896C528.376 278.712 527.912 278.404 527.584 277.972C527.264 277.54 527.104 277.032 527.104 276.448C527.104 275.976 527.208 275.568 527.416 275.224C527.632 274.872 527.932 274.6 528.316 274.408C528.7 274.208 529.152 274.108 529.672 274.108C530.44 274.108 531.056 274.324 531.52 274.756C531.984 275.18 532.22 275.748 532.228 276.46H531.148C531.148 276.028 531.016 275.692 530.752 275.452C530.496 275.204 530.132 275.08 529.66 275.08C529.196 275.08 528.832 275.192 528.568 275.416C528.312 275.64 528.184 275.952 528.184 276.352C528.184 276.712 528.28 277.024 528.472 277.288C528.664 277.552 528.94 277.74 529.3 277.852L530.5 278.236C531.084 278.42 531.536 278.732 531.856 279.172C532.176 279.612 532.336 280.128 532.336 280.72C532.336 281.2 532.224 281.62 532 281.98C531.776 282.34 531.46 282.62 531.052 282.82C530.652 283.02 530.184 283.12 529.648 283.12ZM537.515 283C536.947 283 536.503 282.848 536.183 282.544C535.871 282.24 535.715 281.812 535.715 281.26V277.384H533.855V276.4H535.715V274.54H536.795V276.4H539.435V277.384H536.795V281.26C536.795 281.764 537.035 282.016 537.515 282.016H539.315V283H537.515ZM542.527 285.16L543.499 282.592L541.051 276.4H542.251L543.787 280.48C543.843 280.616 543.895 280.78 543.943 280.972C543.991 281.156 544.031 281.312 544.063 281.44C544.095 281.312 544.135 281.156 544.183 280.972C544.231 280.78 544.279 280.616 544.327 280.48L545.767 276.4H546.931L543.679 285.16H542.527ZM552.086 283C551.702 283 551.366 282.924 551.078 282.772C550.79 282.62 550.566 282.404 550.406 282.124C550.246 281.844 550.166 281.516 550.166 281.14V275.224H547.946V274.24H551.246V281.14C551.246 281.412 551.322 281.628 551.474 281.788C551.626 281.94 551.83 282.016 552.086 282.016H554.186V283H552.086ZM555.801 283V282.016H558.141V277.384H556.101V276.4H559.221V282.016H561.441V283H555.801ZM558.561 275.212C558.297 275.212 558.089 275.144 557.937 275.008C557.785 274.872 557.709 274.688 557.709 274.456C557.709 274.216 557.785 274.028 557.937 273.892C558.089 273.748 558.297 273.676 558.561 273.676C558.825 273.676 559.033 273.748 559.185 273.892C559.337 274.028 559.413 274.216 559.413 274.456C559.413 274.688 559.337 274.872 559.185 275.008C559.033 275.144 558.825 275.212 558.561 275.212ZM563.081 283V276.4H564.161V277.66H564.389L564.161 277.912C564.161 277.392 564.317 276.992 564.629 276.712C564.941 276.424 565.373 276.28 565.925 276.28C566.589 276.28 567.117 276.484 567.509 276.892C567.901 277.3 568.097 277.856 568.097 278.56V283H567.017V278.68C567.017 278.208 566.889 277.848 566.633 277.6C566.385 277.344 566.045 277.216 565.613 277.216C565.165 277.216 564.809 277.352 564.545 277.624C564.289 277.896 564.161 278.288 564.161 278.8V283H563.081ZM571.104 285.16V284.176H573.204C573.54 284.176 573.784 284.1 573.936 283.948C574.096 283.796 574.176 283.56 574.176 283.24V282.4L574.2 281.2H573.984L574.188 281.02C574.188 281.5 574.028 281.88 573.708 282.16C573.388 282.44 572.96 282.58 572.424 282.58C571.744 282.58 571.208 282.36 570.816 281.92C570.424 281.472 570.228 280.872 570.228 280.12V278.728C570.228 277.976 570.424 277.38 570.816 276.94C571.208 276.5 571.744 276.28 572.424 276.28C572.96 276.28 573.388 276.42 573.708 276.7C574.028 276.98 574.188 277.36 574.188 277.84L573.984 277.66H574.188V276.4H575.256V283.24C575.256 283.832 575.072 284.3 574.704 284.644C574.344 284.988 573.84 285.16 573.192 285.16H571.104ZM572.748 281.644C573.196 281.644 573.548 281.504 573.804 281.224C574.06 280.944 574.188 280.556 574.188 280.06V278.8C574.188 278.304 574.06 277.916 573.804 277.636C573.548 277.356 573.196 277.216 572.748 277.216C572.292 277.216 571.936 277.352 571.68 277.624C571.432 277.896 571.308 278.288 571.308 278.8V280.06C571.308 280.572 571.432 280.964 571.68 281.236C571.936 281.508 572.292 281.644 572.748 281.644Z"
              fill="#444449"
            />
            <path
              d="M256.104 283V274.24H258.816C259.336 274.24 259.792 274.348 260.184 274.564C260.576 274.772 260.88 275.064 261.096 275.44C261.312 275.816 261.42 276.256 261.42 276.76C261.42 277.352 261.264 277.86 260.952 278.284C260.648 278.708 260.232 279 259.704 279.16L261.54 283H260.268L258.588 279.28H257.184V283H256.104ZM257.184 278.308H258.816C259.264 278.308 259.624 278.168 259.896 277.888C260.168 277.6 260.304 277.224 260.304 276.76C260.304 276.288 260.168 275.912 259.896 275.632C259.624 275.352 259.264 275.212 258.816 275.212H257.184V278.308ZM265.795 283.12C265.275 283.12 264.819 283.016 264.427 282.808C264.043 282.6 263.743 282.308 263.527 281.932C263.319 281.548 263.215 281.104 263.215 280.6V278.8C263.215 278.288 263.319 277.844 263.527 277.468C263.743 277.092 264.043 276.8 264.427 276.592C264.819 276.384 265.275 276.28 265.795 276.28C266.315 276.28 266.767 276.384 267.151 276.592C267.543 276.8 267.843 277.092 268.051 277.468C268.267 277.844 268.375 278.288 268.375 278.8V279.964H264.271V280.6C264.271 281.12 264.403 281.52 264.667 281.8C264.931 282.072 265.307 282.208 265.795 282.208C266.211 282.208 266.547 282.136 266.803 281.992C267.059 281.84 267.215 281.616 267.271 281.32H268.351C268.279 281.872 268.007 282.312 267.535 282.64C267.071 282.96 266.491 283.12 265.795 283.12ZM267.319 279.244V278.8C267.319 278.28 267.187 277.88 266.923 277.6C266.667 277.32 266.291 277.18 265.795 277.18C265.307 277.18 264.931 277.32 264.667 277.6C264.403 277.88 264.271 278.28 264.271 278.8V279.148H267.403L267.319 279.244ZM272.031 283V278.044H270.111V277.06H272.031V275.92C272.031 275.4 272.195 274.992 272.523 274.696C272.851 274.392 273.303 274.24 273.879 274.24H275.811V275.224H273.879C273.631 275.224 273.439 275.284 273.303 275.404C273.175 275.516 273.111 275.688 273.111 275.92V277.06H275.811V278.044H273.111V283H272.031ZM277.606 283V282.016H279.946V277.384H277.906V276.4H281.026V282.016H283.246V283H277.606ZM280.366 275.212C280.102 275.212 279.894 275.144 279.742 275.008C279.59 274.872 279.514 274.688 279.514 274.456C279.514 274.216 279.59 274.028 279.742 273.892C279.894 273.748 280.102 273.676 280.366 273.676C280.63 273.676 280.838 273.748 280.99 273.892C281.142 274.028 281.218 274.216 281.218 274.456C281.218 274.688 281.142 274.872 280.99 275.008C280.838 275.144 280.63 275.212 280.366 275.212ZM284.885 283V276.4H285.965V277.66H286.193L285.965 277.912C285.965 277.392 286.121 276.992 286.433 276.712C286.745 276.424 287.177 276.28 287.729 276.28C288.393 276.28 288.921 276.484 289.313 276.892C289.705 277.3 289.901 277.856 289.901 278.56V283H288.821V278.68C288.821 278.208 288.693 277.848 288.437 277.6C288.189 277.344 287.849 277.216 287.417 277.216C286.969 277.216 286.613 277.352 286.349 277.624C286.093 277.896 285.965 278.288 285.965 278.8V283H284.885ZM294.577 283.12C294.057 283.12 293.601 283.016 293.209 282.808C292.825 282.6 292.525 282.308 292.309 281.932C292.101 281.548 291.997 281.104 291.997 280.6V278.8C291.997 278.288 292.101 277.844 292.309 277.468C292.525 277.092 292.825 276.8 293.209 276.592C293.601 276.384 294.057 276.28 294.577 276.28C295.097 276.28 295.549 276.384 295.933 276.592C296.325 276.8 296.625 277.092 296.833 277.468C297.049 277.844 297.157 278.288 297.157 278.8V279.964H293.053V280.6C293.053 281.12 293.185 281.52 293.449 281.8C293.713 282.072 294.089 282.208 294.577 282.208C294.993 282.208 295.329 282.136 295.585 281.992C295.841 281.84 295.997 281.616 296.053 281.32H297.133C297.061 281.872 296.789 282.312 296.317 282.64C295.853 282.96 295.273 283.12 294.577 283.12ZM296.101 279.244V278.8C296.101 278.28 295.969 277.88 295.705 277.6C295.449 277.32 295.073 277.18 294.577 277.18C294.089 277.18 293.713 277.32 293.449 277.6C293.185 277.88 293.053 278.28 293.053 278.8V279.148H296.185L296.101 279.244ZM298.964 283V276.4H299.912V277.192H300.128L299.96 277.48C299.96 277.112 300.056 276.82 300.248 276.604C300.448 276.388 300.716 276.28 301.052 276.28C301.42 276.28 301.704 276.416 301.904 276.688C302.104 276.96 302.204 277.344 302.204 277.84L301.88 277.192H302.384L302.156 277.48C302.156 277.112 302.256 276.82 302.456 276.604C302.664 276.388 302.936 276.28 303.272 276.28C303.688 276.28 304.008 276.436 304.232 276.748C304.464 277.06 304.58 277.472 304.58 277.984V283H303.584V277.972C303.584 277.7 303.524 277.488 303.404 277.336C303.292 277.184 303.124 277.108 302.9 277.108C302.684 277.108 302.512 277.184 302.384 277.336C302.264 277.48 302.204 277.688 302.204 277.96V283H301.34V277.972C301.34 277.692 301.276 277.48 301.148 277.336C301.028 277.184 300.856 277.108 300.632 277.108C300.408 277.108 300.24 277.184 300.128 277.336C300.016 277.48 299.96 277.688 299.96 277.96V283H298.964ZM308.967 283.12C308.447 283.12 307.991 283.016 307.599 282.808C307.215 282.6 306.915 282.308 306.699 281.932C306.491 281.548 306.387 281.104 306.387 280.6V278.8C306.387 278.288 306.491 277.844 306.699 277.468C306.915 277.092 307.215 276.8 307.599 276.592C307.991 276.384 308.447 276.28 308.967 276.28C309.487 276.28 309.939 276.384 310.323 276.592C310.715 276.8 311.015 277.092 311.223 277.468C311.439 277.844 311.547 278.288 311.547 278.8V279.964H307.443V280.6C307.443 281.12 307.575 281.52 307.839 281.8C308.103 282.072 308.479 282.208 308.967 282.208C309.383 282.208 309.719 282.136 309.975 281.992C310.231 281.84 310.387 281.616 310.443 281.32H311.523C311.451 281.872 311.179 282.312 310.707 282.64C310.243 282.96 309.663 283.12 308.967 283.12ZM310.491 279.244V278.8C310.491 278.28 310.359 277.88 310.095 277.6C309.839 277.32 309.463 277.18 308.967 277.18C308.479 277.18 308.103 277.32 307.839 277.6C307.575 277.88 307.443 278.28 307.443 278.8V279.148H310.575L310.491 279.244ZM313.667 283V276.4H314.747V277.66H314.975L314.747 277.912C314.747 277.392 314.903 276.992 315.215 276.712C315.527 276.424 315.959 276.28 316.511 276.28C317.175 276.28 317.703 276.484 318.095 276.892C318.487 277.3 318.683 277.856 318.683 278.56V283H317.603V278.68C317.603 278.208 317.475 277.848 317.219 277.6C316.971 277.344 316.631 277.216 316.199 277.216C315.751 277.216 315.395 277.352 315.131 277.624C314.875 277.896 314.747 278.288 314.747 278.8V283H313.667ZM324.078 283C323.51 283 323.066 282.848 322.746 282.544C322.434 282.24 322.278 281.812 322.278 281.26V277.384H320.418V276.4H322.278V274.54H323.358V276.4H325.998V277.384H323.358V281.26C323.358 281.764 323.598 282.016 324.078 282.016H325.878V283H324.078Z"
              fill="#444449"
            />
          </g>
          {/* Canvas Box */}
          <g id="box">
            <rect
              filter="blur(10px)"
              opacity="0.2"
              x="522.5"
              y="32.5"
              width="59"
              height="59"
              rx="9.5"
              fill="black"
            />
            <rect
              x="522.5"
              y="32.5"
              width="59"
              height="59"
              rx="9.5"
              fill="url(#box-fill-gradient)"
              stroke="url(#box-stroke-gradient)"
            />
            <g ref={canvasIndicatorRef} fill="white">
              <circle filter="blur(4px)" opacity="0.8" cx="572" cy="42" r="3" />
              <circle cx="572" cy="42" r="3" />
            </g>
            <path
              d="M530 79.8791V76.1263C530 75.3225 530.429 74.5796 531.125 74.1778L534.375 72.3014C535.071 71.8995 535.929 71.8996 536.625 72.3014L539.875 74.1778C539.962 74.228 540.045 74.2836 540.123 74.3439C540.672 74.7663 541 75.423 541 76.1263V79.689C541 79.8164 541.003 79.9449 540.992 80.0705C540.93 80.7995 540.516 81.4579 539.875 81.8276L536.625 83.704C535.929 84.1059 535.071 84.1059 534.375 83.704L531.125 81.8276C530.429 81.4258 530 80.6829 530 79.8791Z"
              fill="#575757"
              fillOpacity="0.4"
            />
            <path
              d="M535 79.8791V76.1263C535 75.3225 535.429 74.5796 536.125 74.1778L539.375 72.3014C540.071 71.8995 540.929 71.8996 541.625 72.3014L544.875 74.1778C544.962 74.228 545.045 74.2836 545.123 74.3439C545.672 74.7663 546 75.423 546 76.1263V79.689C546 79.8164 546.003 79.9449 545.992 80.0705C545.93 80.7995 545.516 81.4579 544.875 81.8276L541.625 83.704C540.929 84.1059 540.071 84.1059 539.375 83.704L536.125 81.8276C535.429 81.4258 535 80.6829 535 79.8791Z"
              fill="#575757"
            />
          </g>
          {/* Concept Box */}
          <g id="box">
            <rect
              filter="blur(10px)"
              opacity="0.2"
              x="769.5"
              y="32.5"
              width="59"
              height="59"
              rx="9.5"
              fill="black"
            />
            <rect
              x="769.5"
              y="32.5"
              width="59"
              height="59"
              rx="9.5"
              fill="url(#box-fill-gradient)"
              stroke="url(#box-stroke-gradient)"
            />
            <g ref={conceptIndicatorRef} fill="#38393B">
              <circle filter="blur(4px)" opacity="0.8" cx="819" cy="42" r="3" />
              <circle cx="819" cy="42" r="3" />
            </g>
            <path
              d="M781.9 70C780.46 70 779.274 71.0869 779.117 72.4855C777.893 72.8715 777 74.0072 777 75.3667C777 76.9065 778.4 77.9333 779.8 77.9333C779.333 78.4 778.485 78.5723 777.614 78.2462C777.519 78.5123 777.467 78.7994 777.467 79.1C777.467 80.1971 778.155 81.1201 779.115 81.4877C779.259 82.8992 780.45 84 781.9 84C783.06 84 784 83.0598 784 81.9V72.1C784 70.9402 783.06 70 781.9 70Z"
              fill="#575757"
            />
            <path
              d="M786.1 70C787.54 70 788.726 71.0869 788.883 72.4855C790.107 72.8715 791 74.0072 791 75.3667C791 76.9065 789.6 77.9333 788.2 77.9333C788.667 78.4 789.515 78.5723 790.386 78.2462C790.481 78.5123 790.533 78.7994 790.533 79.1C790.533 80.1971 789.845 81.1201 788.885 81.4877C788.741 82.8992 787.55 84 786.1 84C784.94 84 784 83.0598 784 81.9V72.1C784 70.9402 784.94 70 786.1 70Z"
              fill="#575757"
              fillOpacity="0.4"
            />
          </g>
          {/* Design Drafting Box */}
          <g id="box">
            <rect
              filter="blur(10px)"
              opacity="0.2"
              x="769.5"
              y="207.5"
              width="59"
              height="59"
              rx="9.5"
              fill="black"
            />
            <rect
              x="769.5"
              y="207.5"
              width="59"
              height="59"
              rx="9.5"
              fill="url(#box-fill-gradient)"
              stroke="url(#box-stroke-gradient)"
            />
            <g ref={designIndicatorRef} fill="#38393B">
              <circle filter="blur(4px)" opacity="0.8" cx="819" cy="217" r="3" />
              <circle cx="819" cy="217" r="3" />
            </g>

            <path
              d="M791.024 252.012C791.024 248.1 787.819 244.935 783.892 245.001C780.241 245.062 777.158 248.064 777.006 251.713C776.849 255.492 779.685 258.644 783.339 258.992C783.975 259.053 784.551 258.746 784.907 258.301C785.372 257.72 785.445 256.917 785.092 256.263L784.884 255.876C784.69 255.517 784.951 255.08 785.359 255.08H787.956C789.651 255.08 791.024 253.707 791.024 252.012Z"
              fill="#575757"
              fillOpacity="0.4"
            />
            <path
              d="M787.731 249.843C787.303 250.271 786.609 250.271 786.181 249.843C785.753 249.415 785.753 248.721 786.181 248.293C786.609 247.865 787.303 247.865 787.731 248.293C788.159 248.721 788.159 249.415 787.731 249.843Z"
              fill="#575757"
            />
            <path
              d="M778.753 252.012C778.753 252.617 779.243 253.108 779.849 253.108C780.454 253.108 780.944 252.617 780.944 252.012C780.944 251.407 780.454 250.917 779.849 250.917C779.243 250.917 778.753 251.407 778.753 252.012Z"
              fill="#575757"
            />
            <path
              d="M780.293 249.843C780.721 250.271 781.415 250.271 781.843 249.843C782.271 249.415 782.271 248.721 781.843 248.293C781.415 247.865 780.721 247.865 780.293 248.293C779.865 248.721 779.865 249.415 780.293 249.843Z"
              fill="#575757"
            />
            <path
              d="M782.916 247.849C782.916 248.454 783.407 248.944 784.012 248.944C784.617 248.944 785.108 248.454 785.108 247.849C785.108 247.244 784.617 246.753 784.012 246.753C783.407 246.753 782.916 247.244 782.916 247.849Z"
              fill="#575757"
            />
          </g>
          {/* Styling Box */}
          <g id="box">
            <rect
              filter="blur(10px)"
              opacity="0.2"
              x="522.5"
              y="207.5"
              width="59"
              height="59"
              rx="9.5"
              fill="black"
            />
            <rect
              x="522.5"
              y="207.5"
              width="59"
              height="59"
              rx="9.5"
              fill="url(#box-fill-gradient)"
              stroke="url(#box-stroke-gradient)"
            />
            <g ref={stylingIndicatorRef} fill="#38393B">
              <circle filter="blur(4px)" opacity="0.8" cx="572" cy="217" r="3" />
              <circle cx="572" cy="217" r="3" />
            </g>

            <path
              d="M538.83 246.612H533.992C533.534 246.612 533.111 246.813 532.802 247.144C532.122 247.869 531.45 248.603 530.757 249.316C530.709 249.366 530.615 249.463 530.543 249.559C530.125 250.114 530.116 250.883 530.543 251.45C531.143 252.246 531.744 253.043 532.344 253.839C533.546 255.433 534.749 257.027 535.947 258.624C535.965 258.648 535.999 258.693 536.037 258.737C536.693 259.493 537.899 259.464 538.513 258.649L543.936 251.45C544.167 251.144 544.27 250.779 544.251 250.42H540.698L538.83 246.612Z"
              fill="#575757"
              fillOpacity="0.4"
            />
            <path
              d="M530.36 251.138C530.167 250.702 530.184 250.202 530.403 249.781H544.076C544.295 250.201 544.312 250.702 544.119 251.138H530.36Z"
              fill="#575757"
            />
            <path
              d="M535.742 258.351L533.422 250.655C533.371 250.486 533.389 250.303 533.47 250.146L535.099 247.022C535.106 247.007 535.113 246.992 535.12 246.978L535.311 246.612H536.844L534.802 250.528C535.612 253.214 536.426 255.9 537.234 258.587L539.677 250.528L537.636 246.612H539.168L539.359 246.978C539.365 246.99 539.371 247.003 539.377 247.015L541.009 250.146C541.091 250.303 541.108 250.486 541.056 250.656L538.714 258.382L538.513 258.649C537.899 259.464 536.693 259.493 536.037 258.737C535.999 258.693 535.965 258.648 535.947 258.624C535.879 258.533 535.81 258.442 535.742 258.351Z"
              fill="#575757"
            />
            <path
              d="M530.679 247.066C531.053 247.066 531.357 246.762 531.357 246.387C531.357 246.012 531.053 245.708 530.679 245.708C530.304 245.708 530 246.012 530 246.387C530 246.762 530.304 247.066 530.679 247.066Z"
              fill="#575757"
            />
            <path
              d="M543.583 246.451L542.669 246.144L542.364 245.223C542.266 244.926 541.776 244.926 541.677 245.223L541.372 246.144L540.459 246.451C540.311 246.5 540.21 246.639 540.21 246.797C540.21 246.955 540.311 247.094 540.459 247.143L541.372 247.45L541.677 248.371C541.727 248.52 541.865 248.62 542.02 248.62C542.176 248.62 542.315 248.519 542.363 248.371L542.668 247.45L543.582 247.143C543.73 247.094 543.83 246.955 543.83 246.797C543.83 246.639 543.73 246.5 543.582 246.451H543.583Z"
              fill="#575757"
            />
          </g>
          {/* Refinement Box */}
          <g id="box">
            <rect
              filter="blur(10px)"
              opacity="0.2"
              x="261.5"
              y="207.5"
              width="59"
              height="59"
              rx="9.5"
              fill="black"
            />
            <rect
              x="261.5"
              y="207.5"
              width="59"
              height="59"
              rx="9.5"
              fill="url(#box-fill-gradient)"
              stroke="url(#box-stroke-gradient)"
            />
            <g ref={refinementIndicatorRef} fill="#38393B">
              <circle filter="blur(4px)" opacity="0.8" cx="311" cy="217" r="3" />
              <circle cx="311" cy="217" r="3" />
            </g>

            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M277.941 245.387C278.087 245.094 278.418 244.941 278.736 245.021L281.536 245.721C281.911 245.815 282.14 246.195 282.046 246.57C281.952 246.945 281.572 247.173 281.197 247.079L278.942 246.515L277.949 248.5L278.942 250.485L281.197 249.921C281.572 249.827 281.952 250.055 282.046 250.43C282.14 250.805 281.911 251.185 281.536 251.279L278.736 251.979C278.418 252.059 278.087 251.907 277.941 251.613L276.734 249.2H272.967C272.58 249.2 272.267 248.887 272.267 248.5C272.267 248.113 272.58 247.8 272.967 247.8H276.734L277.941 245.387Z"
              fill="#575757"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M269 256.433C269 255.532 269.732 254.8 270.633 254.8H280.433C281.335 254.8 282.067 255.532 282.067 256.433V257.367C282.067 258.269 281.335 259 280.433 259H270.633C269.732 259 269 258.269 269 257.367V256.433Z"
              fill="#575757"
              fillOpacity="0.4"
            />
            <path
              d="M272.695 254.8L271.216 250.363C271.255 250.366 271.294 250.367 271.333 250.367C271.795 250.367 272.218 250.199 272.545 249.92L274.171 254.8H272.695Z"
              fill="#575757"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M269.233 248.5C269.233 247.341 270.174 246.4 271.333 246.4C272.493 246.4 273.433 247.341 273.433 248.5C273.433 249.659 272.493 250.6 271.333 250.6C270.174 250.6 269.233 249.659 269.233 248.5Z"
              fill="#575757"
            />
          </g>
          <defs>
            <radialGradient
              id="box-fill-gradient"
              cx="0.5"
              cy="0.5"
              r="0.7"
              gradientUnits="objectBoundingBox"
            >
              <stop offset="0" stopColor="#1A1B1C" />
              <stop offset="1" stopColor="#151515" />
            </radialGradient>
            <linearGradient id="box-stroke-gradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="#636363" />
              <stop offset="0.3" stopColor="#2D2E2F" />
            </linearGradient>
          </defs>
        </svg>
      </div>
    </div>
  );
};

export default ProjectDeliveringAnimation;
// 668 x 240 px

function handleIndicatorAnimation(
  indicatorRef: React.RefObject<SVGCircleElement | null>,
  type: "default" | "pending" | "completed" | "off",
) {
  let color = "#38393B";

  switch (type) {
    case "default":
      color = "#ffffff";
      break;
    case "pending":
      color = "#FFB900";
      break;
    case "completed":
      color = "#00C950";
      break;
    case "off":
      color = "#38393B";
      break;
  }

  gsap.to(indicatorRef.current, {
    fill: color,
    duration: 0.6,
    ease: "power2.inOut",
  });
}
